import { ProposalFieldTraversalSource } from "work/proposals/negotiation/proposal-field-history-request-context";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * Whether arriving at a field leaves the Comments pane open on that field. Arriving never opens it.
 *
 * **The pane opens only when the reader asks for it** (#6625): the header Comments button, the
 * unread-comments stop in "Prev / Next" (#5399), or a link that names a conversation
 * (`?pane=comments`, #5826). Focusing a field, clicking it, pressing its Edit button, or resolving a
 * change on it — Accept, Reject, Propose alternative, ↶ — is the reader asking to work on the field,
 * not to read the conversation. The pane is seated in the page's row rather than over it, so opening it
 * takes width from the field they reached for, and the first thing they had to do was close it again.
 *
 * **A field reporting its own traverse-in keeps an open pane open**, and moves it to that field, so a
 * reader who is reading the conversation and clicks a field does not lose it.
 *
 * **A "Prev / Next Change" press on a field closes it.** What a field stop is about is answered where
 * the stop is: an unread change opens that field's own history popover, and an issue scrolls its
 * highlighted row into view. The conversation has a stop of its own instead (#5399), which is not a
 * field and never reaches this rule — see `proposal-traversal-stops` and the pages'
 * `handleTraverseToUnreadComments`.
 *
 * A fee-schedule *category* closes it either way (#5118): its row sits in the widest table on the
 * page, and the pane takes its width from that table. The id-less whole-schedule field is not an
 * exception: a stop there means there are no categories at all, so there is no table to narrow.
 */
export function proposalTraversalKeepsCommentsPaneOpen(args: {
  source: ProposalFieldTraversalSource;
  field: ProposalField;
  commentsPaneIsOpen: boolean;
}): boolean {
  if (!args.commentsPaneIsOpen) return false;
  if (args.source === ProposalFieldTraversalSource.Traverser) return false;
  return !args.field.isFeeScheduleCategory;
}
